import { useState } from "react"
import { ProductionSlip } from "../../../../merchant/merchant/production/ordersForCalendar/ordersForCalendarTypes"
import { DateTime } from "luxon"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { createProductionSlip } from "../../../../merchant/merchant/production/ordersForCalendar/ordersForCalendarApi"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { DatesSetArg, EventClickArg } from "@fullcalendar/core"
import { Grid } from "@mui/material"
import { StandardCalendar } from "../../../../../shared/components/calendar/StandardCalendar"
import { ProductionSlipDialog } from "../../../../merchant/merchant/production/ordersForCalendar/components/ProductionSlipDialog"
import { makeStyles } from "tss-react/mui"
import { fetchUserCalendarEvents, FetchUserCalendarEventsResponse, UserDetails } from "../../../nonAdminUserApi"
import { useAppSelector } from "../../../../store/storeHooks"
import { selectUserId } from "../../../../login/loginSelectors"
import { selectEffectiveCompanyId, selectEffectiveUserId } from "../../../../context/contextSelectors"
import { CalendarEvent } from "../../../../../shared/components/calendar/calendarTypes"
import { OrderChatDrawer } from "../../../../../shared/components/orderChat/OrderChatDrawer"
import { ChatMessageAuthorRole, UserType } from "../../../../../shared/components/orderChat/orderChatTypes"
import { UserMeetingsReceipt } from "../../MeetingsReceipt/UserMeetingsReceipt"

import { useDateTime } from "../../../../localization/useDateTime"

const useStyles = makeStyles()((theme) => ({
    container: {
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: "0.75rem",
        gap: "1.25rem",
        marginTop: "3rem",
        padding: "3rem 1rem 1rem 1rem",
    },
}))

const selectOrders = (data: FetchUserCalendarEventsResponse): CalendarEvent[] => {
    return (
        data?.orders.map((order) => ({
            id: order.id,
            uid: order.uid,
            title: order.calendarDetails.title,
            start: order.calendarDetails.start,
            end: order.calendarDetails.end,
            meetingDetails: {
                meetingName: order.calendarDetails.title,
            },
            deliveryDetails: {
                time: order.calendarDetails.start,
            },
            extendedProps: {
                uid: order.uid,
                orderSummary: order.orderSummary,
                color: order.calendarDetails.color,
                participants: order.orderSummary.participants,
                bgColor: order.calendarDetails.bgColor,
                location: order.calendarDetails.location,
            },
        })) || []
    )
}

export function UserReportsCalendar() {
    const { classes } = useStyles()
    const userId = useAppSelector(selectUserId)
    const effectiveUserId = useAppSelector(selectEffectiveUserId)
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const dateTimeFactory = useDateTime()

    const [orderModalState, setOrderModalState] = useState<{
        show: boolean
        list: null | ProductionSlip[]
    }>({
        show: false,
        list: null,
    })

    const [chatDialogOrder, setChatDialogOrder] = useState<{ id: number; title: string } | null>(null)
    const [receiptOrderId, setReceiptOrderId] = useState<number | null>(null)

    const [startDate, setStartDate] = useState<DateTime>(DateTime.now().startOf("month"))
    const [endDate, setEndDate] = useState<DateTime>(DateTime.now().endOf("month"))

    const { data: events = [] } = useApiQuery(fetchUserCalendarEvents, {
        queryName: UserDetails.USER_CALENDAR_EVENTS,
        dependencies: {
            userId: userId ?? 0,
            startDate: startDate.toISODate(),
            endDate: endDate.toISODate(),
        },
        select: selectOrders,
        enabled: false,  // FIXME: Temporary disable until endpoint can be fixed
    })

    const { mutate: callProductionSlip } = useApiMutation(createProductionSlip, {
        async onSuccess(_, data) {
            setOrderModalState({
                show: true,
                list: data.orders,
            })
        },
    })

    const handleDatesSet = (datesSetInfo: DatesSetArg) => {
        setStartDate(dateTimeFactory.fromJSDate(datesSetInfo.start))
        setEndDate(dateTimeFactory.fromJSDate(datesSetInfo.end))
    }

    const handleEventClick = (clickInfo: EventClickArg) => {
        const orderId = Number(clickInfo.event.id)
        orderId && callProductionSlip({ orderIds: [orderId] })
    }

    return (
        <Grid container flexDirection="column" className={classes.container}>
            <StandardCalendar
                events={events}
                handleDatesSet={handleDatesSet}
                onChatOpen={(id, title) => setChatDialogOrder({ id, title })}
            />
            {chatDialogOrder !== null && (
                <OrderChatDrawer
                    open
                    onClose={() => setChatDialogOrder(null)}
                    orderId={chatDialogOrder.id}
                    orderTitle={chatDialogOrder.title}
                    viewerRole={ChatMessageAuthorRole.CUSTOMER}
                    sender={
                        effectiveUserId && companyId
                            ? { type: UserType.USER, companyId, userId: effectiveUserId }
                            : undefined
                    }
                    onSubtitleClick={() => setReceiptOrderId(chatDialogOrder.id)}
                />
            )}
            {receiptOrderId !== null && (
                <UserMeetingsReceipt
                    open
                    id={receiptOrderId}
                    onClose={() => setReceiptOrderId(null)}
                />
            )}
            {orderModalState.show && orderModalState.list && (
                <ProductionSlipDialog
                    list={orderModalState.list}
                    open={orderModalState.show}
                    onClose={() =>
                        setOrderModalState({
                            show: false,
                            list: null,
                        })
                    }
                />
            )}
        </Grid>
    )
}
